import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconSearch } from "@tabler/icons-react";
import { cn } from "@/lib/cn";
import { getRegistry } from "@/components/toolbox/registry";
import type { FlowSection, ToolboxItem } from "@/components/toolbox/types";
import { dispatchToolboxInsert } from "@/components/toolbox/insertHandler";
import { MegaFooter, readShowLatexPref, writeShowLatexPref } from "./MegaFooter";

// ds24 Handoff 26 — Insert mega menu (Surface 2). A wide, grouped, searchable
// block-insert panel opened from the ribbon's "Insert ▾". Reads the SAME
// toolbox registry the Toolbox uses (one source of insert actions, Q6), so
// every item maps to a real, wired block kind.

interface InsertMenuProps {
	open: boolean;
	onClose: () => void;
	/** The trigger button — the menu is portalled to <body> and anchored
	 *  under it (fixed), so the ribbon's overflow-x clip can't cut it off. */
	anchorEl?: HTMLElement | null;
}

const MENU_W = 620;

// Section order + ds24-aligned labels. The registry's FlowSection taxonomy is
// the source; these are just display labels + ordering for the mega panel.
const SECTIONS: { key: FlowSection; label: string }[] = [
	{ key: "text", label: "Structure & text" },
	{ key: "math", label: "Math & proof" },
	{ key: "media", label: "Media & data" },
	{ key: "lists", label: "Lists" },
	{ key: "refs", label: "References" },
	{ key: "layout", label: "Layout" },
	{ key: "inline", label: "Inline" },
];

// One-line descriptions per item slug (ds24: icon + label + description).
const HINTS: Record<string, string> = {
	paragraph: "Body text",
	"heading-1": "Top-level section title",
	"heading-2": "Subsection title",
	"heading-3": "Sub-subsection title",
	blockquote: "Quoted passage",
	callout: "Note / tip / warning admonition",
	"list-bullet": "Unordered list",
	"list-numbered": "Ordered list",
	"list-task": "Checklist",
	equation: "Display math ($$…$$)",
	theorem: "Numbered theorem environment",
	lemma: "Numbered lemma",
	proof: "Proof environment (□)",
	definition: "Numbered definition",
	image: "Image with caption",
	figure: "Figure with caption + label",
	"figure-tikz": "Drawn from TikZ · starts from a template",
	"figure-tikz-ask": "Describe it · Ask Lilia draws it in TikZ",
	"existing-table": "One of your tables — link it or take a copy",
	video: "Embedded video",
	code: "Code listing with language",
	citation: "Inline \\cite reference",
	"cross-ref": "Reference to a label",
	footnote: "Footnote",
	bibliography: "Reference list",
	columns: "Multi-column layout",
	"page-break": "Force a new page",
};

// ds28 §3.4 — representative LaTeX each item emits, keyed by registry slug.
// Previewed in the mega footer on hover/focus. Unmapped slugs fall back to ""
// (footer shows the placeholder).
const LATEX_BY_SLUG: Record<string, string> = {
	paragraph: "(text)",
	"heading-1": "\\section{…}",
	"heading-2": "\\subsection{…}",
	"heading-3": "\\subsubsection{…}",
	"page-break": "\\newpage",
	toc: "\\tableofcontents",
	equation: "\\begin{equation} … \\end{equation}",
	theorem: "\\begin{theorem} … \\end{theorem}",
	lemma: "\\begin{lemma} … \\end{lemma}",
	proof: "\\begin{proof} … \\end{proof}",
	definition: "\\begin{definition} … \\end{definition}",
	figure: "\\begin{figure} … \\end{figure}",
	"figure-tikz": "\\begin{tikzpicture} … \\end{tikzpicture}",
	image: "\\begin{figure} … \\end{figure}",
	table: "\\begin{tabular}{…} … \\end{tabular}",
	code: "\\begin{lstlisting} … \\end{lstlisting}",
	blockquote: "\\begin{quote} … \\end{quote}",
	callout: "\\begin{tcolorbox} … \\end{tcolorbox}",
	bibliography: "\\begin{thebibliography} …",
	citation: "\\cite{key}",
	"list-bullet": "\\begin{itemize} … \\end{itemize}",
	"list-numbered": "\\begin{enumerate} … \\end{enumerate}",
	"list-task": "\\begin{itemize} … \\end{itemize}",
	"inline-math": "$…$",
	footnote: "\\footnote{…}",
};

export function InsertMenu({ open, onClose, anchorEl }: InsertMenuProps) {
	const [query, setQuery] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const registry = useMemo(() => getRegistry("flow"), []);
	// ds28 §3.4 — LaTeX preview footer state.
	const [hoveredLatex, setHoveredLatex] = useState("");
	const [showLatex, setShowLatex] = useState(readShowLatexPref);

	// Anchor position (fixed coords under the trigger), clamped to the viewport.
	const [pos, setPos] = useState<{ top: number; left: number }>({ top: 56, left: 12 });
	useEffect(() => {
		if (!open || !anchorEl) return;
		const r = anchorEl.getBoundingClientRect();
		const left = Math.max(8, Math.min(r.left, window.innerWidth - MENU_W - 8));
		setPos({ top: r.bottom + 4, left });
	}, [open, anchorEl]);

	// Reset and focus when the menu opens — only then. onClose is an inline
	// arrow in the ribbon, a new function on every render; with it in these
	// deps, any re-render while the menu was open (an autosave, an editor
	// update) ran the reset again and wiped what the author was typing into
	// the search. Read it through a ref instead.
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	useEffect(() => {
		if (!open) return;
		setQuery("");
		setHoveredLatex("");
		const t = setTimeout(() => searchRef.current?.focus(), 30);
		const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCloseRef.current(); };
		window.addEventListener("keydown", onKey);
		return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
	}, [open]);

	const sections = useMemo(() => {
		const q = query.trim().toLowerCase();
		return SECTIONS.map(({ key, label }) => {
			const items = (registry[key] ?? []).filter((it) =>
				!q || it.label.toLowerCase().includes(q) || (HINTS[it.slug] ?? "").toLowerCase().includes(q),
			);
			return { key, label, items };
		}).filter((s) => s.items.length > 0);
	}, [registry, query]);

	const isEmpty = sections.length === 0;

	if (!open) return null;

	const activate = (it: ToolboxItem) => {
		if (!it.supported) return;
		dispatchToolboxInsert({ kind: it.kind, variant: it.variant }, "toolbox");
		onClose();
	};

	return createPortal(
		<>
			<div
				data-testid="insert-backdrop"
				className="fixed inset-0 z-[60]"
				onClick={onClose}
			/>
			<div
				data-testid="insert-menu"
				role="menu"
				aria-label="Insert block"
				className="fixed z-[61] overflow-hidden rounded-[14px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)]"
				style={{ top: pos.top, left: pos.left, width: MENU_W, maxWidth: "92vw", boxShadow: "0 22px 60px var(--lilia-shadow-lg)" }}
			>
				{/* Search */}
				<div className="flex items-center gap-2 border-b border-[var(--lilia-border)] px-3 h-10">
					<IconSearch size={15} className="shrink-0 text-[var(--lilia-text-muted)]" />
					<input
						ref={searchRef}
						data-testid="insert-search"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Filter blocks…"
						className="w-full bg-transparent text-[13px] text-[var(--lilia-text)] outline-none placeholder:text-[var(--lilia-text-muted)]"
					/>
				</div>

				{isEmpty ? (
					<div data-testid="insert-empty" className="px-4 py-10 text-center text-[13px] text-[var(--lilia-text-muted)]">
						No blocks match “{query}”. Try equation, table, or theorem.
					</div>
				) : (
					<div
						className="grid grid-cols-2 gap-x-2 gap-y-3 max-h-[60vh] overflow-y-auto p-3"
						onMouseLeave={() => setHoveredLatex("")}
					>
						{sections.map((sec) => (
							<div key={sec.key} data-testid="insert-section" data-section={sec.key} className="min-w-0">
								<div className="px-2 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)]">
									{sec.label}
								</div>
								{sec.items.map((it) => {
									const Icon = it.icon;
									return (
										<button
											key={it.slug}
											type="button"
											role="menuitem"
											data-testid={`insert-item-${it.slug}`}
											disabled={!it.supported}
											onClick={() => activate(it)}
											onMouseEnter={() => setHoveredLatex(LATEX_BY_SLUG[it.slug] ?? "")}
											onFocus={() => setHoveredLatex(LATEX_BY_SLUG[it.slug] ?? "")}
											className={cn(
												"flex w-full items-start gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors",
												it.supported
													? "hover:bg-[var(--lilia-hover)] cursor-pointer"
													: "opacity-45 cursor-not-allowed",
											)}
										>
											<Icon size={16} className="mt-0.5 shrink-0 text-[var(--lilia-text-secondary)]" />
											<span className="min-w-0">
												<span className="flex items-center gap-1.5">
													<span className="text-[13px] font-medium text-[var(--lilia-text)]">{it.label}</span>
													{it.kind === "citation" && (
														<span className="rounded bg-[var(--lilia-primary-subtle)] px-1 text-[9px] font-semibold uppercase tracking-wide text-[var(--lilia-primary)]">inline</span>
													)}
													{!it.supported && (
														<span className="text-[9.5px] uppercase tracking-wide text-[var(--lilia-text-muted)]">soon</span>
													)}
												</span>
												<span className="block truncate text-[11px] text-[var(--lilia-text-muted)]">{HINTS[it.slug] ?? ""}</span>
											</span>
										</button>
									);
								})}
							</div>
						))}
					</div>
				)}

				{/* ds28 §3.4 — LaTeX preview footer pinned to the bottom. */}
				<MegaFooter
					hoveredLatex={hoveredLatex}
					showLatex={showLatex}
					onToggle={(next) => {
						setShowLatex(next);
						writeShowLatexPref(next);
					}}
				/>
			</div>
		</>,
		document.body,
	);
}
